import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hammer, Plus, Trash2, ArrowLeft, Info } from "lucide-react";
import { BrutalCard, BrutalButton, SectionTitle } from "@/components/Brutal";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";

export default function KlussenPool() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useT();
  const admin = isAdmin(user);
  const [enabled, setEnabled] = useState(false);
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!admin) return;
    api.get("/rewards/catchup-config")
      .then(({ data }) => { setEnabled(!!data.catchup_enabled); setPool(data.catchup_pool || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [admin]);

  if (!admin) {
    return (
      <div className="space-y-4" data-testid="klussen-pool-noaccess">
        <SectionTitle>{t("Klussen Pool")}</SectionTitle>
        <BrutalCard color="cream"><p className="font-bold">{t("Alleen een beheerder kan de Klussen Pool beheren.")}</p></BrutalCard>
      </div>
    );
  }

  const addItem = () => setPool((p) => [...p, { title: "", points: 20 }]);
  const updItem = (i, patch) => setPool((p) => p.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const delItem = (i) => setPool((p) => p.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = pool.map((x) => ({ title: (x.title || "").trim(), points: Math.max(0, parseInt(x.points || 0, 10) || 0) })).filter((x) => x.title);
      const { data } = await api.put("/rewards/catchup-config", { catchup_enabled: enabled, catchup_pool: cleaned });
      setPool(data.catchup_pool || []);
      toast.success(t("Klussen Pool opgeslagen"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Opslaan mislukt"));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="klussen-pool-page">
      <button data-testid="klussen-pool-back" onClick={() => navigate("/meer")} className="flex items-center gap-1.5 text-sm font-bold text-brand-ink/60"><ArrowLeft className="w-4 h-4" strokeWidth={2.5} />{t("Terug")}</button>
      <SectionTitle sub={t("Tweede kans: extra klussen om punten in te halen")}>{t("Klussen Pool")}</SectionTitle>

      <BrutalCard color="cream">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 mt-0.5 shrink-0 text-brand-violet" strokeWidth={2.5} />
          <p className="text-sm font-semibold text-brand-ink/70">{t("Wie deze week zijn drempel niet haalt, kan een inhaalklus uit de pool oppakken. Bij het afvinken krijgt de uitvoerder precies de punten die je hier instelt.")}</p>
        </div>
      </BrutalCard>

      <BrutalCard>
        <label className="flex items-center justify-between">
          <span className="font-head font-bold flex items-center gap-2"><Hammer className="w-5 h-5 text-brand-orange" strokeWidth={2.5} />{t("Inhaalklussen aanzetten")}</span>
          <input type="checkbox" data-testid="klussen-pool-toggle" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-6 h-6" />
        </label>
      </BrutalCard>

      <div className="space-y-2" data-testid="klussen-pool-list">
        <div className="flex items-center justify-between">
          <p className="font-head font-bold text-sm">{t("Klussen in de pool")}</p>
          <button data-testid="klussen-pool-add" onClick={addItem} className="brutal-sm brutal-press rounded-full px-3 py-1.5 text-xs font-bold bg-brand-yellow flex items-center gap-1"><Plus className="w-3.5 h-3.5" strokeWidth={2.5} />{t("Klus")}</button>
        </div>
        {loading ? (
          <p className="text-sm text-brand-ink/40">{t("Laden…")}</p>
        ) : pool.length === 0 ? (
          <BrutalCard color="cream"><p className="text-sm font-semibold text-brand-ink/50">{t("Nog geen inhaalklussen. Voeg er een toe met de knop hierboven.")}</p></BrutalCard>
        ) : pool.map((it, i) => (
          <div key={i} data-testid={`klussen-pool-item-${i}`} className="brutal-sm rounded-xl bg-white p-3 flex items-center gap-2">
            <input data-testid={`klussen-pool-title-${i}`} value={it.title} onChange={(e) => updItem(i, { title: e.target.value })} placeholder={t("Titel (bv. Ramen zemen)")}
              className="flex-1 min-w-0 brutal-sm rounded-lg px-3 py-2 font-medium outline-none bg-brand-cream" />
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-brand-violet font-black">+</span>
              <input data-testid={`klussen-pool-points-${i}`} type="number" min="0" value={it.points} onChange={(e) => updItem(i, { points: e.target.value })}
                className="w-20 brutal-sm rounded-lg px-2 py-2 font-bold outline-none bg-brand-cream text-center" />
            </div>
            <button data-testid={`klussen-pool-del-${i}`} onClick={() => delItem(i)} className="brutal-sm brutal-press rounded-full p-2 bg-white shrink-0"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>
          </div>
        ))}
      </div>

      <BrutalButton data-testid="klussen-pool-save" onClick={save} disabled={saving} className="w-full">{saving ? t("Bezig…") : t("Opslaan")}</BrutalButton>
    </div>
  );
}
